import React from 'react'

import type { PaginationProps, TableHeaders, TableProps } from '@fourthwall/components'
import { useMediaQueryContext, usePagination } from '@fourthwall/components'
import type { GetPostsParams } from '@fourthwall/services/dist/MembershipsCreatorApiClient'
import { useShopTime } from '@modules/Timezone/providers/ShopTimeProvider'

import { reverse, routing } from '../../../../../../utils/routing'
import type { Post } from '../../../types'
import { useTiersQuery } from '../../Perks/hooks/useTiersQuery'
import { DateCell } from '../components/DateCell'
import { PostCell } from '../components/PostCell'
import { getSubtitle } from '../utils/mapAccessTierNames'
import { useFilterPosts } from './useFilterPosts'
import { usePostsQuery } from './usePostsQuery'

export const usePosts = (state: GetPostsParams['state']) => {
  const { filtersParams, searchQueryParam } = useFilterPosts()
  const media = useMediaQueryContext()
  const { timezoneAbbreviation } = useShopTime()

  const types = filtersParams.types?.split(',') as GetPostsParams['types[]']

  const [currentPage, setCurrentPage] = usePagination()
  const { postsQuery } = usePostsQuery(
    [
      {
        state,
        page: currentPage,
        'types[]': types,
        search_query: searchQueryParam || undefined,
        limit: 20,
      },
    ],
    {
      refetchInterval: (data) =>
        data?.posts.some((post) => post.statuses.resources === 'Processing') ? 60000 : false,
    },
  )
  const { tiersQuery } = useTiersQuery()

  const getPostCell = (post: Post) => {
    const tiers = tiersQuery.data?.tiers

    if ('image' in post && post.image && 'thumbnailUrl' in post.image) {
      return (
        <PostCell
          type={post.type}
          title={post.title}
          thumbnailUrl={post.image.thumbnailUrl}
          resources={post.statuses.resources}
          subtitle={getSubtitle(
            post.access,
            tiers,
            post.type === 'LiveStream' ? post.streamStartsAt : undefined,
          )}
          pinned={!!post.pinnedAt}
        />
      )
    }

    return (
      <PostCell
        type={post.type}
        title={post.title}
        resources={post.statuses.resources}
        subtitle={getSubtitle(
          post.access,
          tiers,
          post.type === 'LiveStream' ? post.streamStartsAt : undefined,
        )}
        pinned={!!post.pinnedAt}
      />
    )
  }

  const mobileHeaders: TableHeaders<Post> = [
    {
      id: 'post',
      label: undefined,
      render: getPostCell,
    },
  ]

  const headers: TableHeaders<Post> = [
    {
      id: 'date',
      label: `Date (${timezoneAbbreviation})`,
      render: ({ liveAt }) => <DateCell date={liveAt} />,
      width: 3,
    },
    {
      id: 'post',
      label: 'Post',
      render: getPostCell,
      width: 18,
    },
    {
      id: 'comments',
      label: 'Comments',
      render: ({ comments }) => comments.count,
      width: 2,
    },
  ]

  const table: TableProps = {
    data: postsQuery.data?.posts || [],
    headers: media.laptop ? headers : mobileHeaders,
    // @ts-ignore
    getRowLink: ({ id }: { id: string }) => ({
      pathname: reverse(routing.memberships.posts.post.self, { postId: id }),
      search: location.search,
    }),
  }

  const pagination: PaginationProps = {
    pageCount: postsQuery.data?.pagination?.pages || 1,
    currentPage,
    onChange: setCurrentPage,
  }

  return {
    posts: {
      table,
      pagination,
      isLoading: postsQuery.isFetching,
    },
  }
}
